import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  getCollectionVoteInfo,
  submitVoteRequest,
} from "../../services/learningApi";
import "./Vote.css";

const CollectionVote = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [voteResult, setVoteResult] = useState(null); // { kind, message }

  const loadCollection = useCallback(async () => {
    if (!collectionId) return;
    setLoading(true);
    setError("");
    setErrorCode("");
    setVoteResult(null);
    try {
      const response = await getCollectionVoteInfo({
        collectionId,
        includeUserStatus: true,
      });
      const info = response?.data;
      if (!info) {
        setError("اطلاعاتی برای این کالکشن یافت نشد");
        return;
      }
      setData(info);
    } catch (err) {
      console.error("load collection error:", err);
      setError(err?.message || "خطا در دریافت اطلاعات کالکشن");
      setErrorCode(err?.errorCode || "");
    } finally {
      setLoading(false);
    }
  }, [collectionId]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  const handleVote = async () => {
    if (!data || submitting) return;
    setSubmitting(true);
    setVoteResult(null);
    try {
      const result = await submitVoteRequest({
        collectionId: data.collectionId,
        subtitleUploadId: data.subOneID,
      });
      if (result.alreadyExists) {
        setVoteResult({
          kind: "info",
          message: "برای این زیرنویس قبلاً محتوای یادگیری استخراج شده است.",
        });
      } else if (result.alreadyVoted) {
        setVoteResult({
          kind: "success",
          message: "درخواست شما قبلاً برای این کالکشن دو زیرنویس ثبت شده است.",
        });
      } else {
        setVoteResult({
          kind: "success",
          message: "درخواست استخراج لغات و اصطلاحات با موفقیت ثبت شد.",
        });
      }
      // به‌روزرسانی وضعیت صفحه با پاسخ جدید
      await loadCollection();
    } catch (err) {
      setVoteResult({
        kind: "danger",
        message: err?.message || "خطا در ثبت درخواست. لطفاً بعداً تلاش کنید.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const videoTypeLabel =
    data?.videoType === "series" ? "سریال" : "فیلم";

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <button
          className="vote-btn vote-btn-sm"
          onClick={() => {
            if (location.key === "default") {
              navigate("/vote", { replace: true });
            } else {
              navigate(-1);
            }
          }}
        >
          <i className="fas fa-arrow-right"></i>
          بازگشت
        </button>
      </div>

      {loading && (
        <div className="vote-state">
          <div className="spinner"></div>
          <p>در حال دریافت اطلاعات…</p>
        </div>
      )}

      {!loading && error && (
        <div className="vote-state">
          <i className="fas fa-triangle-exclamation vote-state-icon"></i>
          <p>{error}</p>
          {errorCode === "COLLECTION_NOT_FOUND" && (
            <p style={{ fontSize: "0.85rem", marginTop: 8 }}>
              شناسه واردشده معتبر نیست.
            </p>
          )}
          <button
            className="vote-btn vote-btn-primary"
            style={{ marginTop: 16 }}
            onClick={loadCollection}
          >
            <i className="fas fa-rotate-right"></i>
            تلاش مجدد
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Movie hero */}
          <section className="vote-collection-hero">
            <div className="vote-collection-cover">
              {data.coverUrl ? (
                <img src={data.coverUrl} alt={data.movieName || ""} />
              ) : (
                <i className="fas fa-film"></i>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 className="vote-collection-title">
                {data.movieName || "نامشخص"}
              </h2>
              <p className="vote-collection-sub">{data.collectionId}</p>
              <div className="vote-collection-chips">
                <span className="vote-chip teal">
                  <i className="fas fa-film"></i>
                  {videoTypeLabel}
                </span>
                {data.videoType === "series" && (
                  <span className="vote-chip gray">
                    <i className="fas fa-tv"></i>
                    فصل {data.season ?? "؟"} · قسمت {data.episode ?? "؟"}
                  </span>
                )}
                {data.langOne && (
                  <span className="vote-chip small">
                    <i className="fas fa-language"></i>
                    زبان: {data.langOne}
                  </span>
                )}
                {data.collectionType && (
                  <span className="vote-chip small">
                    <i className="fas fa-tag"></i>
                    {data.collectionType === "umlang" ? "UMLANG" : "کاربری"}
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* Rejected alert */}
          {data.rejected && (
            <div className="vote-alert vote-alert-danger">
              <i className="fas fa-circle-xmark"></i>
              <span>
                این مجموعه زیرنویس تأیید نشده است؛ بنابراین نمی‌توان برای آن
                درخواست ثبت کرد.
              </span>
            </div>
          )}

          {/* Action box */}
          <section className="vote-action-box">
            <h3 className="vote-action-title">
              <i className="fas fa-file-lines"></i>
              زیرنویس شماره ۱
            </h3>
            <p className="vote-action-desc">
              درخواست استخراج لغات و اصطلاحات برای زیرنویس انگلیسی این مجموعه
              ثبت می‌شود. با ثبت درخواست به اولویت‌بندی این زیرنویس برای
              پردازش کمک می‌کنید.
            </p>

            <div className="vote-pair">
              <div className="vote-pair-card">
                <div className="vote-pair-value">{data.aggregate?.activeVotes ?? 0}</div>
                <div className="vote-pair-label">رأی فعال</div>
              </div>
              <div className="vote-pair-card alt">
                <div className="vote-pair-value">{data.aggregate?.totalVotes ?? 0}</div>
                <div className="vote-pair-label">کل درخواست‌ها</div>
              </div>
              <div className="vote-pair-card muted">
                <div
                  className="vote-pair-value"
                  style={{ fontSize: "1.05rem", direction: "ltr" }}
                >
                  {data.subOneID || "—"}
                </div>
                <div className="vote-pair-label">شناسه زیرنویس</div>
              </div>
            </div>

            {voteResult && (
              <div className={`vote-alert vote-alert-${voteResult.kind}`}>
                <i
                  className={`fas ${
                    voteResult.kind === "danger"
                      ? "fa-circle-xmark"
                      : voteResult.kind === "info"
                      ? "fa-circle-info"
                      : "fa-circle-check"
                  }`}
                ></i>
                <span>{voteResult.message}</span>
              </div>
            )}

            {data.learningExists && (
              <div className="vote-alert vote-alert-success">
                <i className="fas fa-circle-check"></i>
                <span>
                  محتوای آموزشی این زیرنویس
                  {data.learningStatus === "completed"
                    ? " آماده است."
                    : ` در حال آماده‌سازی است (${data.learningStatus}).`}
                </span>
              </div>
            )}

            {data.userHasVoted && !data.learningExists && (
              <div className="vote-alert vote-alert-info">
                <i className="fas fa-circle-info"></i>
                <span>شما قبلاً برای این کالکشن دو زیرنویس درخواست ثبت کرده‌اید.</span>
              </div>
            )}

            <button
              className="vote-btn vote-btn-primary vote-btn-full"
              onClick={handleVote}
              disabled={
                submitting ||
                data.rejected ||
                data.learningExists ||
                data.userHasVoted ||
                !data.subOneID
              }
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  در حال ثبت…
                </>
              ) : data.learningExists ? (
                <>
                  <i className="fas fa-circle-check"></i>
                  درخواست ثبت نشد (محتوای یادگیری موجود است)
                </>
              ) : data.userHasVoted ? (
                <>
                  <i className="fas fa-circle-check"></i>
                  درخواست شما ثبت شده است
                </>
              ) : data.rejected ? (
                <>
                  <i className="fas fa-ban"></i>
                  امکان ثبت درخواست وجود ندارد
                </>
              ) : (
                <>
                  <i className="fas fa-hand-pointer"></i>
                  ثبت درخواست استخراج لغات و اصطلاحات
                </>
              )}
            </button>
          </section>
        </>
      )}
    </>
  );
};

export default CollectionVote;